import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert } from '../atoms/Alert/Alert';
import { Button } from '../atoms/Button/Button';
import { Checkbox } from '../atoms/Checkbox/Checkbox';
import { Select } from '../atoms/Select/Select';
import { LiveRegion } from '../atoms/LiveRegion/LiveRegion';
import { Stack } from '../atoms/Stack/Stack';
import { Text } from '../atoms/Typography/Text';
import { LoadErrorState } from '../feedback/LoadErrorState/LoadErrorState';
import { Section } from '../templates/Section/Section';
import { useTranslation } from '../../i18n/useTranslation';
import { ApiError } from '../../lib/apiClient';
import {
  EMAIL_PREFERENCES_QUERY_KEY,
  fetchEmailPreferences,
  resendConfirmationEmail,
  timeZoneOptions,
  uiLocale,
  updateEmailPreferences,
  type EmailPreferences,
} from '../../services/emailPreferencesService';

/** Anchor of this section — linked from the task form and from every e-mail footer. */
export const EMAIL_NOTIFICATIONS_ANCHOR = 'email-notifications';

export interface EmailNotificationsSectionProps {
  className?: string;
}

type Field = 'productUpdates' | 'taskReminders' | 'criticalAlerts' | 'quietHours';
type Notice = 'saved' | 'confirmationSent' | 'resent' | null;
type Failure = 'save' | 'resend' | 'resendRateLimited' | 'resendUnavailable' | null;

/**
 * Settings → "E-Mail-Benachrichtigungen": product updates / release notes
 * (double opt-in: "Bestätigung ausstehend – E-Mail erneut senden") and task
 * reminders. A change is saved at once (PATCH /api/v1/me/email-preferences,
 * with the UI language for the e-mails) and announced in a polite live region;
 * failures are an Alert, never colour only.
 */
export function EmailNotificationsSection({ className = '' }: EmailNotificationsSectionProps) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const prefs = useQuery({ queryKey: EMAIL_PREFERENCES_QUERY_KEY, queryFn: fetchEmailPreferences, retry: false });
  const [saving, setSaving] = useState<Field | null>(null);
  const [resending, setResending] = useState(false);
  const [notice, setNotice] = useState<Notice>(null);
  const [failure, setFailure] = useState<Failure>(null);
  const loaded = prefs.data !== undefined;

  // Links to /settings#email-notifications (task form, e-mail footers) land here.
  useEffect(() => {
    if (!loaded || typeof window === 'undefined' || window.location.hash !== `#${EMAIL_NOTIFICATIONS_ANCHOR}`) return;
    document.getElementById(EMAIL_NOTIFICATIONS_ANCHOR)?.scrollIntoView?.({ block: 'start' });
  }, [loaded]);

  async function toggle(field: Field, value: boolean): Promise<void> {
    setSaving(field);
    setNotice(null);
    setFailure(null);
    try {
      const updated = await updateEmailPreferences({ [field]: value, locale: uiLocale(i18n.language) });
      queryClient.setQueryData<EmailPreferences>(EMAIL_PREFERENCES_QUERY_KEY, updated);
      setNotice(field === 'productUpdates' && value && updated.productUpdatesStatus === 'pending_confirmation' ? 'confirmationSent' : 'saved');
    } catch {
      setFailure('save');
    } finally {
      setSaving(null);
    }
  }

  async function changeTimeZone(zone: string): Promise<void> {
    setNotice(null);
    setFailure(null);
    try {
      const updated = await updateEmailPreferences({ timezone: zone, locale: uiLocale(i18n.language) });
      queryClient.setQueryData<EmailPreferences>(EMAIL_PREFERENCES_QUERY_KEY, updated);
      setNotice('saved');
    } catch {
      setFailure('save');
    }
  }

  async function resend(): Promise<void> {
    setResending(true);
    setNotice(null);
    setFailure(null);
    try {
      await resendConfirmationEmail();
      setNotice('resent');
    } catch (err: unknown) {
      if (err instanceof ApiError && err.status === 429) setFailure('resendRateLimited');
      else if (err instanceof ApiError && err.status === 503) setFailure('resendUnavailable');
      else setFailure('resend');
    } finally {
      setResending(false);
    }
  }

  let content;
  if (prefs.isPending) {
    content = (
      <LiveRegion busy>
        <Text size="sm" color="muted">{t('settings.email.loading')}</Text>
      </LiveRegion>
    );
  } else if (prefs.isError || prefs.data === undefined) {
    content = (
      <LoadErrorState
        compact
        titleLevel={3}
        title={t('settings.email.loadError')}
        onRetry={() => void prefs.refetch()}
        retryLabel={t('settings.email.retry')}
        retrying={prefs.isFetching}
      />
    );
  } else {
    const data = prefs.data;
    const confirmedAt = data.productUpdatesConfirmedAt
      ? new Intl.DateTimeFormat(i18n.language, { day: 'numeric', month: 'long', year: 'numeric' }).format(
          new Date(data.productUpdatesConfirmedAt),
        )
      : null;
    content = (
      <Stack gap="6">
        {!data.emailDeliveryAvailable && (
          <Alert tone="info" live="off" compact>
            {t('settings.email.deliveryUnavailable')}
          </Alert>
        )}

        <Stack gap="2">
          <Checkbox
            id="email-product-updates"
            label={t('settings.email.productUpdates')}
            checked={data.productUpdates}
            disabled={saving !== null}
            onChange={(e) => void toggle('productUpdates', e.target.checked)}
            aria-describedby="email-product-updates-status"
          />
          <div id="email-product-updates-status">
            {data.productUpdatesStatus === 'pending_confirmation' && (
              <Stack gap="2" align="start">
                <Text size="sm">{t('settings.email.pending')}</Text>
                <Button variant="secondary" size="sm" onClick={() => void resend()} isLoading={resending}>
                  {t('settings.email.resend')}
                </Button>
              </Stack>
            )}
            {data.productUpdatesStatus === 'confirmed' && (
              <Text size="sm" color="muted">
                {confirmedAt ? t('settings.email.confirmedOn', { date: confirmedAt }) : t('settings.email.confirmed')}
              </Text>
            )}
            {data.productUpdatesStatus === 'off' && (
              <Text size="sm" color="muted">{t('settings.email.productUpdatesHelp')}</Text>
            )}
          </div>
        </Stack>

        <Stack gap="2">
          <Checkbox
            id="email-task-reminders"
            label={t('settings.email.taskReminders')}
            checked={data.taskReminders}
            disabled={saving !== null}
            onChange={(e) => void toggle('taskReminders', e.target.checked)}
            aria-describedby="email-task-reminders-help"
          />
          <Text id="email-task-reminders-help" size="sm" color="muted">
            {t('settings.email.taskRemindersHelp')}
          </Text>
        </Stack>

        <Stack gap="2">
          <Checkbox
            id="email-critical-alerts"
            label={t('settings.email.criticalAlerts')}
            checked={data.criticalAlerts === true}
            disabled={saving !== null}
            onChange={(e) => void toggle('criticalAlerts', e.target.checked)}
            aria-describedby="email-critical-alerts-help"
          />
          <Text id="email-critical-alerts-help" size="sm" color="muted">
            {t('settings.email.criticalAlertsHelp')}
          </Text>
          {data.criticalAlerts === true && (
            <Stack gap="2">
              <Checkbox
                id="email-quiet-hours"
                label={t('settings.email.quietHours')}
                checked={data.quietHours === true}
                disabled={saving !== null}
                onChange={(e) => void toggle('quietHours', e.target.checked)}
              />
              <Select
                id="email-timezone"
                label={t('settings.email.timezone')}
                value={data.timezone ?? 'Europe/Berlin'}
                onChange={(e) => void changeTimeZone(e.target.value)}
                options={timeZoneOptions(Intl.DateTimeFormat().resolvedOptions().timeZone).map((zone) => ({ value: zone, label: zone }))}
              />
            </Stack>
          )}
        </Stack>

        {failure !== null && (
          <Alert compact onDismiss={() => setFailure(null)} dismissLabel={t('common.close')}>
            {t(`settings.email.errors.${failure}`)}
          </Alert>
        )}
        <LiveRegion>
          {notice !== null ? (
            <Alert tone="success" live="off" compact>
              {t(`settings.email.notice.${notice}`)}
            </Alert>
          ) : null}
        </LiveRegion>
      </Stack>
    );
  }

  return (
    <Section
      id={EMAIL_NOTIFICATIONS_ANCHOR}
      title={t('settings.email.title')}
      description={t('settings.email.description')}
      className={className || undefined}
    >
      {content}
    </Section>
  );
}

export default EmailNotificationsSection;
